import { useCallback } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	isIntentDetectionSuccess,
	jiraIntentDetectionQuery,
	KnowledgeDiscoveryJiraIntentDetectionModel,
	KnowledgeDiscoveryJiraSearchExperience,
} from '@atlassian/search-client';

import { sha256Hash } from '../../../../common/utils/events/utils';
import {
	onTaskFail,
	onTaskStart,
	onTaskSuccess,
	remapVitalStatsEventForQuickFind,
	TASK_NAMES,
} from '../../../../common/utils/events/vital-stats-events';
import { serveJiraNINFpsNLQNavigation } from '../../../../common/utils/experiments/experiment-exports';
import { IntentMode } from '../../../../common/utils/intent-detection';
import { onIntentDetectionResponse } from '../../../../common/utils/quick-find/events/intent-detection-response';
import { useQuickFindAnalytics } from '../../../../controllers/quick-find/analytics';
import { useSearchSessionId } from '../../../../controllers/store/bootstrap';
import {
	useCloudId,
	useOrgId,
	usePrimaryProduct,
} from '../../../../controllers/store/bootstrap/accessors';

import { modelIdValidator } from './utils';

/**
 * Real intent detection via @atlassian/search-client.
 */
export const useIntentDetectionService = ({
	defaultMode = IntentMode.GNIN,
}: { defaultMode?: IntentMode } = {}) => {
	const [cloudId] = useCloudId();
	const [orgId] = useOrgId();
	const [primaryProduct] = usePrimaryProduct();
	const [searchSessionId] = useSearchSessionId();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const intentDetectionModelId = serveJiraNINFpsNLQNavigation({
		primaryProduct,
		fireLayerExposure: false,
	})?.modelId;

	const fireAnalytics = useCallback(
		(action: 'success' | 'fail', attributes: Record<string, any> = {}) => {
			fireAnalyticsEvent(
				onIntentDetectionResponse({ action, attributes: { ...attributes, searchSessionId } }),
			);
		},
		[searchSessionId, fireAnalyticsEvent],
	);

	return useCallback(
		async (query: string): Promise<IntentMode> => {
			// create siteARI based on primary product
			const siteARI = `ari:cloud:${primaryProduct}::site/${cloudId}`;
			// plex code uses "jira" as the primary product key but we need to use "jira-software" instead for intent detection
			const jiraSiteARI = `ari:cloud:jira-software::site/${cloudId}`;
			const siteId =
				primaryProduct === 'jira' && fg('kd_fix_jira_intent_siteid') ? jiraSiteARI : siteARI;

			// VitalStats: Generate task ID and record start time
			const taskId = crypto.randomUUID();
			const startTime = performance.now();
			// Query Analytics attributes
			const queryHash = sha256Hash(query);
			const queryLength = query.length;

			fireAnalyticsEvent(
				remapVitalStatsEventForQuickFind(
					onTaskStart({
						searchSessionId: searchSessionId || '',
						taskId,
						task: TASK_NAMES.QUICK_FIND_INTENT_DETECTION_NETWORK,
						moreContext: { queryHash, queryLength },
					}),
				),
			);

			try {
				const response = await jiraIntentDetectionQuery({
					query,
					siteId,
					orgId: orgId ?? '',
					experience: KnowledgeDiscoveryJiraSearchExperience.QUICK_FIND,
					model: modelIdValidator(intentDetectionModelId)
						? intentDetectionModelId
						: KnowledgeDiscoveryJiraIntentDetectionModel.GNIN_HEURISTIC,
				});

				const result = response.data?.knowledgeDiscovery.jiraIntentDetection;
				const latencyMs = performance.now() - startTime;

				if (!isIntentDetectionSuccess(result)) {
					const errorName = 'UnsuccessfulResponse';
					const errorMessage = 'Query typename mismatch';

					fireAnalytics('fail', { queryHash, errorName, errorMessage, queryLength });
					fireAnalyticsEvent(
						remapVitalStatsEventForQuickFind(
							onTaskFail({
								searchSessionId: searchSessionId || '',
								taskId,
								task: TASK_NAMES.QUICK_FIND_INTENT_DETECTION_NETWORK,
								moreContext: {
									queryHash,
									queryLength,
									latencyMs,
									errorName,
									errorMessage,
								},
							}),
						),
					);

					return defaultMode;
				}

				const intentMode = result.searchClassification?.type || defaultMode;

				fireAnalytics('success', { queryHash, queryLength });
				fireAnalyticsEvent(
					remapVitalStatsEventForQuickFind(
						onTaskSuccess({
							searchSessionId: searchSessionId || '',
							taskId,
							task: TASK_NAMES.QUICK_FIND_INTENT_DETECTION_NETWORK,
							moreContext: {
								queryHash,
								queryLength,
								latencyMs,
								queryClassification: intentMode,
							},
						}),
					),
				);

				return intentMode;
			} catch (error) {
				const latencyMs = performance.now() - startTime;
				const errorName = (error as Error)?.name || 'UnknownError';
				const errorMessage = (error as Error)?.message ?? 'An unknown error occurred';

				fireAnalytics('fail', { queryHash, errorName, errorMessage, queryLength });
				fireAnalyticsEvent(
					remapVitalStatsEventForQuickFind(
						onTaskFail({
							searchSessionId: searchSessionId || '',
							taskId,
							task: TASK_NAMES.QUICK_FIND_INTENT_DETECTION_NETWORK,
							moreContext: {
								queryHash,
								queryLength,
								latencyMs,
								errorName: (error as Error)?.name,
								errorMessage: (error as Error)?.message,
							},
						}),
					),
				);

				return defaultMode;
			}
		},
		[
			cloudId,
			orgId,
			primaryProduct,
			defaultMode,
			fireAnalytics,
			searchSessionId,
			fireAnalyticsEvent,
			intentDetectionModelId,
		],
	);
};
